import {
  DATE_RANGE_OPERANDS,
  DROPDOWN_OPERANDS,
  INPUT_NUMBER_OPERANDS,
  INPUT_TEXT_OPERANDS,
} from '../../components/DataGrid/constants/filters';
import type { ColumnFilter } from '../../components/DataGrid/types';
import { ColumnFilterType } from '../../components/DataGrid/types';
import type { DropdownOption } from '../../components/Dropdown/Dropdown';
import { COUNTRIES } from '../../constants/countries';
import { columnDefs } from './columns';

const trueFalseOptions: DropdownOption[] = [
  { value: 'true', label: 'True' },
  { value: 'false', label: 'False' },
];

export const setInputTextFilter = (
  column: ColumnFilter,
  opts?: Partial<ColumnFilter>,
): void => {
  column.type = ColumnFilterType.inputText;

  if (opts?.arrayField) {
    column.arrayField = opts.arrayField;
  }
};

export const setInputNumberFilter = (column: ColumnFilter): void => {
  column.type = ColumnFilterType.inputNumber;
};

export const setDropdownFilter = (
  column: ColumnFilter,
  opts?: Partial<ColumnFilter>,
): void => {
  column.type = ColumnFilterType.dropdown;

  if (opts?.arrayField) {
    column.arrayField = opts.arrayField;
  }

  if (opts?.multiValue) {
    column.multiValue = opts.multiValue;
  }

  if (opts?.customOptions) {
    column.customOptions = opts.customOptions;
  }
};

export const setDateFilter = (column: ColumnFilter): void => {
  column.type = ColumnFilterType.dateRange;
};

export const countryFieldOptions: DropdownOption[] = COUNTRIES.map((c) => ({
  value: c.code,
  label: c.name,
}));

// ToDo: Remove switch, refactor to model approach
export const filterColumns: ColumnFilter[] = columnDefs
  .filter((c) => {
    if (c.field?.startsWith('_')) {
      return false;
    } // Exclude ephemeral columns

    // Add here non-filterable columns
    return true;
  })
  .map((c) => {
    const filterColumn: ColumnFilter = {
      ...c,
      operands: [],
      field: c.field!,
      name: (c.filterName ?? c.headerName)!,
      type: ColumnFilterType.inputText,
      arrayField: false,
      multiValue: false,
    };

    switch (c.field) {
      case 'wmg1Labels':
        filterColumn.type = ColumnFilterType.wmg1Dropdown;
        break;
      case 'artist':
      case 'cleanTitle':
        setInputTextFilter(filterColumn);
        break;
      case 'isImmersive':
        setDropdownFilter(filterColumn, { customOptions: trueFalseOptions });
        break;
      case 'isrcList':
      case 'gpidList':
        setInputTextFilter(filterColumn, { arrayField: true });
        break;
      case 'majorLabelList':
      case 'presentationLabelList':
      case 'repertoireOwnerList':
      case 'shortTitleList':
        setDropdownFilter(filterColumn, { arrayField: true, multiValue: true });
        break;
      case 'trackRightsTerritoryList':
      case 'deliveryTerritoryList':
      case 'missedTerritoriesAlt':
      case 'appleTerritoryList':
      case 'missedAppleTerritoriesAlt':
      case 'spotifyTerritoryList':
      case 'missedSpotifyTerritoriesAlt':
        setDropdownFilter(filterColumn, {
          arrayField: true,
          multiValue: true,
          customOptions: countryFieldOptions,
        });
        break;
      case 'deliveryTerritoryCount':
      case 'missedTerritoriesCountAlt':
      case 'appleTerritoryCount':
      case 'missedAppleTerritoriesCountAlt':
      case 'spotifyTerritoryCount':
      case 'missedSpotifyTerritoriesCountAlt':
      case 'projectedMissedStreams':
        setInputNumberFilter(filterColumn);
        break;
      case undefined:
      default:
        setInputTextFilter(filterColumn);
        break;
    }

    switch (filterColumn.type) {
      case ColumnFilterType.inputText:
        filterColumn.operands = INPUT_TEXT_OPERANDS;
        break;
      case ColumnFilterType.inputNumber:
        filterColumn.operands = INPUT_NUMBER_OPERANDS;
        break;
      case ColumnFilterType.dropdown:
        filterColumn.operands = DROPDOWN_OPERANDS;
        break;
      case ColumnFilterType.dateRange:
        filterColumn.operands = DATE_RANGE_OPERANDS;
        break;
      case undefined:
      default:
        filterColumn.operands = INPUT_TEXT_OPERANDS;
        break;
    }

    return filterColumn;
  });
